import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Visual size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Error message — triggers error styling when truthy */
  error?: string;
  /** Structured options — alternative to passing <option> children */
  options?: SelectOption[];
  /** Placeholder text shown as the first disabled option */
  placeholder?: string;
  children?: ReactNode;
}

const SIZE_MAP = {
  sm: 'h-8 text-2xs px-2.5 pr-7',
  md: 'h-9 text-sm px-3 pr-8',
  lg: 'h-11 text-sm px-3.5 pr-9',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, size = 'md', error, options, placeholder, children, disabled, ...props }, ref) => {
    return (
      <div className="w-full">
        <div className="relative">
          <select
            ref={ref}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            className={clsx(
              'w-full appearance-none rounded-lg border bg-surface-1 text-ink',
              'transition-colors duration-150',
              'focus:outline-hidden focus:border-primary focus:ring-2 focus:ring-primary/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-danger focus:border-danger focus:ring-danger/20',
              !error && 'border-border',
              SIZE_MAP[size],
              className,
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </option>
                ))
              : children}
          </select>
          {/* Custom chevron */}
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-ink-dim">
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>
        {error && <p className="mt-1 text-2xs text-danger">{error}</p>}
      </div>
    );
  },
);

Select.displayName = 'Select';
